import React from "react"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
} from "@coval/ui"
import { Info } from "lucide-react"

import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  FIELD_CONTAINER_STYLES,
  INFO_ICON_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
  TEXT_INPUT_STYLES,
} from "../styles"

interface NonExpressivePausesConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

const PRESET_OPTIONS = [
  {
    key: "strict",
    label: "Strict",
    description: "Detects smaller pauses",
  },
  {
    key: "normal",
    label: "Normal",
    description: "Catches noticeable pauses, ignores quick thinking gaps",
  },
  {
    key: "lenient",
    label: "Lenient",
    description: "Only flags very long pauses",
  },
  {
    key: "custom",
    label: "Custom",
    description: "Define your own thresholds",
  },
]

export const NonExpressivePausesConfig: React.FC<
  NonExpressivePausesConfigProps
> = ({ metricRecordData, onChange }) => {
  const hasLegacyFields =
    !metricRecordData.metric_metadata?.threshold_preset &&
    metricRecordData.metric_metadata &&
    (metricRecordData.metric_metadata.pre_pause_window !== undefined ||
      metricRecordData.metric_metadata.min_pause_duration_seconds !== undefined)
  const currentPreset = hasLegacyFields
    ? "custom"
    : (metricRecordData.metric_metadata?.threshold_preset ?? "normal")
  const isCustom = currentPreset === "custom"

  const handlePresetChange = (value: string) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        threshold_preset: value as "strict" | "normal" | "lenient" | "custom",
      },
    })
  }

  const handlePrePauseWindowChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        pre_pause_window: isNaN(value) ? undefined : value,
      },
    })
  }

  const handleMinPauseDurationSecondsChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        min_pause_duration_seconds: isNaN(value) ? undefined : value,
      },
    })
  }

  const prePauseWindow = metricRecordData.metric_metadata?.pre_pause_window
  const prePauseWindowInvalid =
    prePauseWindow !== undefined &&
    prePauseWindow !== null &&
    !Number.isInteger(prePauseWindow)

  return (
    <div className="space-y-4">
      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <span className={LABEL_STYLES}>Threshold Preset</span>
        </div>
        <Select value={currentPreset} onValueChange={handlePresetChange}>
          <SelectTrigger aria-label="Threshold Preset">
            <SelectValue placeholder="Select preset" />
          </SelectTrigger>
          <SelectContent>
            {PRESET_OPTIONS.map((option) => (
              <SelectItem
                key={option.key}
                value={option.key}
                description={option.description}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-coval-text-muted">
          Select a preset or choose Custom to define your own thresholds.
        </p>
      </div>

      {isCustom && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          <div className={FIELD_CONTAINER_STYLES}>
            <div className={LABEL_ROW_STYLES}>
              <span className={LABEL_STYLES}>Pre-Pause Window (frames)</span>
              <Tooltip
                content="Number of frames to analyze before a pause."
                side="top"
              >
                <button type="button" className="inline-flex" aria-label="Info">
                  <Info className={INFO_ICON_STYLES} />
                </button>
              </Tooltip>
            </div>
            <input
              type="number"
              step="1"
              min="0"
              className={TEXT_INPUT_STYLES}
              value={metricRecordData.metric_metadata?.pre_pause_window ?? ""}
              onChange={handlePrePauseWindowChange}
              placeholder="5"
              aria-label="Pre-Pause Window (frames)"
            />
            {prePauseWindowInvalid && (
              <p className="mt-1 text-sm text-coval-danger-text">
                Pre-Pause Window must be a whole number to save.
              </p>
            )}
          </div>

          <div className={FIELD_CONTAINER_STYLES}>
            <div className={LABEL_ROW_STYLES}>
              <span className={LABEL_STYLES}>Min Pause Duration (seconds)</span>
              <Tooltip
                content="Minimum duration in seconds for a pause to be considered."
                side="top"
              >
                <button type="button" className="inline-flex" aria-label="Info">
                  <Info className={INFO_ICON_STYLES} />
                </button>
              </Tooltip>
            </div>
            <input
              type="number"
              step="0.1"
              min="0"
              className={TEXT_INPUT_STYLES}
              value={
                metricRecordData.metric_metadata?.min_pause_duration_seconds ??
                ""
              }
              onChange={handleMinPauseDurationSecondsChange}
              placeholder="0.6"
              aria-label="Minimum Pause Duration in seconds"
            />
          </div>
        </div>
      )}
    </div>
  )
}
